import React from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../../../utils/formatCurrency";

/**
 * Component to display gateway metadata dynamically
 * Handles various data types and formats them appropriately
 */
const GatewayMetadataDisplay = ({ metadata }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    if (!dateString) return t("N/A");
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const isDateString = (str) => {
    if (typeof str !== "string") return false;
    return /^\d{4}-\d{2}-\d{2}/.test(str) || /^\d{4}-\d{2}-\d{2}T/.test(str);
  };

  const isAmountField = (key) => {
    const amountKeywords = ["amount", "price", "cost", "total", "value", "sum"];
    return amountKeywords.some((keyword) =>
      key.toLowerCase().includes(keyword)
    );
  };

  const isStatusField = (key) => {
    return key.toLowerCase() === "status" || key.toLowerCase().endsWith("_status");
  };

  const isIdField = (key) => {
    return key.toLowerCase().endsWith("_id") || key.toLowerCase().endsWith("id");
  };

  const formatValue = (key, value) => {
    if (value === null || value === undefined) {
      return <span className="text-muted">{t("N/A")}</span>;
    }

    // Handle dates
    if (isDateString(value)) {
      return <span className="text-muted">{formatDate(value)}</span>;
    }

    // Handle amounts
    if (isAmountField(key) && typeof value === "number") {
      return (
        <span className="text-dark fw-semibold">
          {formatCurrency(parseFloat(value))}
        </span>
      );
    }

    // Handle status
    if (isStatusField(key) && typeof value === "string") {
      const statusColors = {
        completed: "success",
        success: "success",
        captured: "success",
        authorised: "info",
        pending: "warning",
        failed: "danger",
        error: "danger",
        cancelled: "secondary",
      };
      const color = statusColors[value.toLowerCase()] || "secondary";
      return (
        <span className={`badge bg-${color} text-white`}>{value}</span>
      );
    }

    // Handle IDs
    if (isIdField(key)) {
      return (
        <code 
          className="text-dark d-block"
          style={{
            wordBreak: "break-all",
            overflowWrap: "break-word",
            fontSize: "0.95rem",
            padding: "0.5rem 0.75rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            maxWidth: "100%",
            display: "block"
          }}
        >
          {value}
        </code>
      );
    }

    // Handle booleans
    if (typeof value === "boolean") {
      return (
        <span className={`badge ${value ? "bg-success" : "bg-secondary"} text-white`}>
          {value ? t("Yes") : t("No")}
        </span>
      );
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <div className="text-center py-2">
            <span className="text-muted">{t("Empty array")}</span>
          </div>
        );
      }
      return (
        <div className="mt-2">
          {value.map((item, idx) => (
            <div
              key={idx}
              className="mb-3 p-3 rounded"
              style={{ 
                backgroundColor: "#f8f9fa",
                wordBreak: "break-word",
                overflowWrap: "break-word"
              }}
            >
              <div className="d-flex align-items-start">
                <span
                  className="badge bg-info text-white me-3 flex-shrink-0"
                  style={{ minWidth: "30px" }}
                >
                  {idx + 1}
                </span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  {typeof item === "object" && item !== null ? (
                    renderObject(item, 0, true)
                  ) : (
                    <span 
                      className="text-dark"
                      style={{
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        fontSize: "0.95rem"
                      }}
                    >
                      {String(item)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Default: string or number
    return (
      <span 
        className="text-dark"
        style={{
          wordBreak: "break-word",
          overflowWrap: "break-word",
          display: "block",
          maxWidth: "100%",
          fontSize: "0.95rem"
        }}
      >
        {String(value)}
      </span>
    );
  };

  const renderObject = (obj, level = 0, isNested = false) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return formatValue("", obj);
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) {
      return (
        <div className="text-center py-2">
          <span className="text-muted">{t("Empty object")}</span>
        </div>
      );
    }

    return (
      <div className={isNested ? "mt-2" : ""}>
        {entries.map(([key, value], idx) => {
          const isNestedObject =
            typeof value === "object" &&
            value !== null &&
            !Array.isArray(value);

          return (
            <div
              key={key}
              className={`mb-3 ${idx < entries.length - 1 ? "border-bottom pb-3" : ""}`}
            >
              <div className="mb-1">
                <strong className="text-dark d-block mb-2">
                  {key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </strong>
              </div>
              <div>
                {isNestedObject ? (
                  <div
                    className="bg-light p-3 rounded"
                    style={{ 
                      backgroundColor: "#f8f9fa",
                      wordBreak: "break-word",
                      overflowWrap: "break-word"
                    }}
                  >
                    {renderObject(value, level + 1, true)}
                  </div>
                ) : (
                  <div 
                    style={{ 
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      fontSize: "0.95rem"
                    }}
                  >
                    {formatValue(key, value)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!metadata || typeof metadata !== "object") {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2"></i>
        {t("No metadata available")}
      </div>
    );
  }

  return (
    <div className="row g-4">
      {Object.entries(metadata).map(([key, value]) => {
        const isNestedObject =
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value);

        // Determine card color based on key
        const getCardColor = (key) => {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes("capture")) return "info";
          if (lowerKey.includes("authorise") || lowerKey.includes("authorize"))
            return "primary";
          if (lowerKey.includes("response")) return "success";
          if (lowerKey.includes("error") || lowerKey.includes("failure"))
            return "danger";
          return "secondary";
        };

        const cardColor = getCardColor(key);
        const cardColors = {
          info: {
            header: "bg-info",
            icon: "fa-check-circle",
          },
          primary: {
            header: "bg-primary",
            icon: "fa-lock",
          },
          success: {
            header: "bg-success",
            icon: "fa-check",
          },
          danger: {
            header: "bg-danger",
            icon: "fa-exclamation-triangle",
          },
          secondary: {
            header: "bg-secondary",
            icon: "fa-database",
          },
        };

        const colorScheme = cardColors[cardColor];

        return (
          <div key={key} className="col-12 col-lg-6">
            <div
              className="card border h-100"
              style={{
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <div
                className={`card-header ${colorScheme.header} text-white`}
                style={{
                  padding: "1rem 1.25rem",
                  borderBottom: "none",
                }}
              >
                <h5 className="mb-0 d-flex align-items-center">
                  <i
                    className={`fas ${colorScheme.icon} me-2`}
                  ></i>
                  <span>
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                </h5>
              </div>
              <div
                className="card-body"
                style={{
                  padding: "1.5rem",
                  backgroundColor: "#fff",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {isNestedObject ? (
                  <div>{renderObject(value)}</div>
                ) : (
                  <div>
                    <div className="text-muted mb-2">{t("Value")}:</div>
                    <div style={{ fontSize: "1rem" }}>{formatValue(key, value)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GatewayMetadataDisplay;

